import React, { type ReactNode, useCallback, useContext } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type {
	CrossFlowInteractedUIActionSubjectIDs,
	CrossFlowViewedUIActionSubjectIDs,
} from '@atlassian/surface-analytics-specs';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { LOOM_CROSS_SELL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

import { type EntryPointId } from './LoomCrossSellPopupCard/BaseLoomPopupCard.tsx';
import { useLoomCrossFlowRequestAccessCallback } from './useLoomCrossFlowRequestAccessCallback.ts';
import { useLoomAdControlState } from './LoomAdControl/useLoomAdControlState.tsx';
import { useLoomCrossJoinUrl } from './useLoomCrossJoinUrl.ts';

const crossFlowAdType = 'crossFlow';
const crossJoinAdType = 'crossJoin';

export const CrossFlowMenuItem = ({
	buttonComponent: ButtonComponent,
	sourceActionSubjectId,
	entryPointId,
	sourceComponent,
	sourceContext,
}: {
	buttonComponent: (props: { onClickBeforeInit: () => void }) => ReactNode;
	// action subject ID of the entrypoint the popup is triggered from
	sourceActionSubjectId: string;
	// a unique identifier for the entrypoint
	entryPointId: EntryPointId;
	sourceComponent: CrossFlowViewedUIActionSubjectIDs;
	sourceContext: CrossFlowInteractedUIActionSubjectIDs;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const triggerCrossFlow = useLoomCrossFlowRequestAccessCallback({
		sourceComponent,
		sourceContext,
	});
	const { isInterestedInLoom } = useLoomAdControlState({
		touchpoint: entryPointId,
	});
	const onCrossFlowTriggerClick = useCallback(
		async (triggerName: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: triggerName,
					source: triggerName === 'tryNow' ? 'loomCrossFlowPopupCard' : sourceActionSubjectId,
					attributes: {
						adType: crossFlowAdType,
						entrypoint: entryPointId,
					},
				},
			}).fire();
			await triggerCrossFlow();
		},
		[createAnalyticsEvent, entryPointId, triggerCrossFlow, sourceActionSubjectId],
	);

	if (!isInterestedInLoom) {
		return null;
	}

	return (
		<ButtonComponent onClickBeforeInit={() => onCrossFlowTriggerClick(sourceActionSubjectId)} />
	);
};

export const CrossJoinMenuItem = ({
	buttonComponent: ButtonComponent,
	sourceActionSubjectId,
	entryPointId,
	sourceComponent,
}: {
	buttonComponent: (props: {
		onClickBeforeInit: () => void;
		href: string | undefined;
		target: string | undefined;
		rel: string | undefined;
	}) => ReactNode;
	// action subject ID of the entrypoint the popup is triggered from
	sourceActionSubjectId: string;
	// a unique identifier for the entrypoint
	entryPointId: EntryPointId;
	sourceComponent?: string;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const crossJoinHref = useLoomCrossJoinUrl();

	const onCrossJoinSuccess = useCallback(() => {
		experienceTracker.succeed({
			name: LOOM_CROSS_SELL_EXPERIENCE,
			attributes: {
				sourceComponent: entryPointId,
				adType: crossJoinAdType,
			},
		});
	}, [entryPointId, experienceTracker]);

	const onCrossJoinTriggerClick = useCallback(async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: sourceActionSubjectId,
				source: sourceActionSubjectId,
				attributes: {
					adType: crossJoinAdType,
					entrypoint: entryPointId,
					sourceComponent,
				},
			},
		}).fire();

		onCrossJoinSuccess();
	}, [
		createAnalyticsEvent,
		entryPointId,
		onCrossJoinSuccess,
		sourceActionSubjectId,
		sourceComponent,
	]);

	return (
		<ButtonComponent
			onClickBeforeInit={onCrossJoinTriggerClick}
			href={crossJoinHref}
			target="_blank"
			rel="noreferrer"
		/>
	);
};
